import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatCurrency, formatPercent } from "@/lib/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#F97316"];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#151B24] border border-[#2D3748] rounded-sm p-3 text-sm shadow-xl">
      <p className="font-semibold text-foreground mb-1">{payload[0]?.name}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-data text-xs text-muted-foreground">
          {p.value > 10000 ? formatCurrency(p.value) : `${p.value}%`}
        </p>
      ))}
    </div>
  );
};

export default function PortfolioOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/portfolio/allocation")
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  const renderDonut = (items, dataKey = "percentage", nameKey = "name") => (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie data={items} dataKey={dataKey} nameKey={nameKey} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} stroke="none">
          {items.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <ReTooltip content={<CustomTooltip />} />
        <Legend verticalAlign="bottom" formatter={(v) => <span className="text-xs text-muted-foreground">{v}</span>} />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderBar = (items, dataKey = "percentage", nameKey = "name") => (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={items} layout="vertical" margin={{ left: 80 }}>
        <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" horizontal={false} />
        <XAxis type="number" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey={nameKey} tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} width={75} />
        <ReTooltip content={<CustomTooltip />} />
        <Bar dataKey={dataKey} fill="#3B82F6" radius={[0, 2, 2, 0]} barSize={18} />
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <div className="p-6 space-y-6" data-testid="portfolio-overview">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Portfolio Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">Allocation analysis across dimensions</p>
      </div>

      <Tabs defaultValue="sector" data-testid="portfolio-tabs">
        <TabsList className="bg-secondary border border-border">
          <TabsTrigger value="sector" data-testid="tab-sector">Sector</TabsTrigger>
          <TabsTrigger value="geography" data-testid="tab-geography">Geography</TabsTrigger>
          <TabsTrigger value="manager" data-testid="tab-manager">Manager</TabsTrigger>
          <TabsTrigger value="fund" data-testid="tab-fund">Fund</TabsTrigger>
        </TabsList>

        <TabsContent value="sector">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">Sector Allocation</CardTitle></CardHeader>
              <CardContent className="p-4">{renderDonut(data?.sector_allocation?.map(s => ({ name: s.name, percentage: s.allocation_pct })) || [])}</CardContent>
            </Card>
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">Sector by Value</CardTitle></CardHeader>
              <CardContent className="p-4">{renderBar(data?.sector_allocation || [], "total_value")}</CardContent>
            </Card>
          </div>
          <Card className="border border-border bg-card mt-4">
            <CardContent className="p-0">
              <table className="w-full dense-table">
                <thead><tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Sector</th>
                  <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Value</th>
                  <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Allocation</th>
                  <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">IRR</th>
                  <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Occupancy</th>
                </tr></thead>
                <tbody>
                  {(data?.sector_allocation || []).map((s, i) => (
                    <tr key={i} className="border-b border-border hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-2 text-sm">{s.name}</td>
                      <td className="px-4 py-2 text-sm font-data text-right">{formatCurrency(s.total_value)}</td>
                      <td className="px-4 py-2 text-sm font-data text-right">{s.allocation_pct}%</td>
                      <td className="px-4 py-2 text-sm font-data text-right text-emerald-500">{s.irr}%</td>
                      <td className="px-4 py-2 text-sm font-data text-right">{s.occupancy}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="geography">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">Geographic Allocation</CardTitle></CardHeader>
              <CardContent className="p-4">{renderDonut(data?.geographic_allocation?.slice(0, 8) || [])}</CardContent>
            </Card>
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">Value by State</CardTitle></CardHeader>
              <CardContent className="p-4">{renderBar(data?.geographic_allocation?.slice(0, 10) || [], "value")}</CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="manager">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">Manager Allocation</CardTitle></CardHeader>
              <CardContent className="p-4">{renderDonut(data?.manager_allocation || [])}</CardContent>
            </Card>
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">AUM by Manager</CardTitle></CardHeader>
              <CardContent className="p-4">{renderBar(data?.manager_allocation || [], "value")}</CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="fund">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">Fund Allocation</CardTitle></CardHeader>
              <CardContent className="p-4">{renderDonut(data?.fund_allocation?.slice(0, 8) || [])}</CardContent>
            </Card>
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">NAV by Fund</CardTitle></CardHeader>
              <CardContent className="p-4">{renderBar(data?.fund_allocation?.slice(0, 8) || [], "value")}</CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
